// components/FullScreenGoogleDocsViewer.jsx - Full-screen viewer for Google Docs translation editing
import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import GoogleDocsEditor from './GoogleDocsEditor';
import DocumentViewer from './DocumentViewer';
import GoogleDocsService from '../services/googleDocsService';
import ApprovalRequestModal from './ApprovalRequestModal';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import useApiClient, { translationApi } from '../services/api';

const FullScreenGoogleDocsViewer = ({
  originalDocument,
  originalDocumentId,
  translatedDocumentId,
  documentType = '',
  documentTitle = 'Document',
  onSave,
  onApprove,
  saving = false,
  translationId, // Add translationId prop for backend call
  readyForApproval = false, // Track if already marked as ready
  sourceLanguage = '',
  targetLanguage = '',
  additionalContext = '',
  persistedRetranslationComments = [],
  latestFinalDocument = null,
  onSign = null
}) => {
  // Mobile detection
  const [isMobile, setIsMobile] = useState(false);

  const [viewMode, setViewMode] = useState('split'); // 'split', 'original-full', 'editor-full'
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [splitRatio, setSplitRatio] = useState(50); // Percentage for left panel
  const [isDragging, setIsDragging] = useState(false);

  // Translation hook
  const { t } = useTranslation();

  // Approval modal state
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [approverName, setApproverName] = useState('');
  const [approverEmail, setApproverEmail] = useState('');
  const [approverLanguage, setApproverLanguage] = useState('fr');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isMarkingReady, setIsMarkingReady] = useState(false);
  const [isReadyForApproval, setIsReadyForApproval] = useState(readyForApproval);
  const [activeTranslatedDocumentId, setActiveTranslatedDocumentId] = useState(translatedDocumentId || null);

  // AI retransation state
  const [showRetranslationDrawer, setShowRetranslationDrawer] = useState(false);
  const [retranslationInput, setRetranslationInput] = useState('');
  const [isRetranslating, setIsRetranslating] = useState(false);
  const [isApplyingRevision, setIsApplyingRevision] = useState(false);
  const [isUndoingRevision, setIsUndoingRevision] = useState(false);
  const [retranslationMessages, setRetranslationMessages] = useState([]);
  const [pendingRevision, setPendingRevision] = useState(null);
  const [revisionHistory, setRevisionHistory] = useState([]);
  const [includeOriginalContext, setIncludeOriginalContext] = useState(true);

  const containerRef = useRef(null);
  const resizerRef = useRef(null);
  const documentViewerRef = useRef(null);
  const viewModeRef = useRef(viewMode);

  // Get Google Auth context for sharing
  const { googleAccessToken, user, userProfile } = useAuth();
  const apiClient = useApiClient();
  const effectiveTranslatedDocumentId = activeTranslatedDocumentId || translatedDocumentId;
  const hasOriginalDocumentContext = Boolean(originalDocument || originalDocumentId);
  const retranslationSessionKey = `retranslation_chat_${translationId || 'unknown'}`;
  const normalizedPersistedRetranslationComments = useMemo(() => {
    if (!Array.isArray(persistedRetranslationComments)) {
      return [];
    }

    return persistedRetranslationComments
      .map((comment) => {
        const content = typeof comment?.content === 'string' ? comment.content.trim() : '';
        const parsedTimestamp = comment?.createdAt ? new Date(comment.createdAt) : null;
        const createdAt = parsedTimestamp && !Number.isNaN(parsedTimestamp.getTime())
          ? parsedTimestamp.toISOString()
          : new Date().toISOString();

        return { content, createdAt };
      })
      .filter((comment) => comment.content.length > 0);
  }, [persistedRetranslationComments]);

  useEffect(() => {
    setActiveTranslatedDocumentId(translatedDocumentId || null);
  }, [translatedDocumentId]);

  // Restore retranslation session from browser session storage
  useEffect(() => {
    if (!translationId) return;
    let hasSessionState = false;

    try {
      const raw = sessionStorage.getItem(retranslationSessionKey);
      hasSessionState = Boolean(raw);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed.messages)) {
          setRetranslationMessages(parsed.messages);
        }
        if (Array.isArray(parsed.revisionHistory)) {
          setRevisionHistory(parsed.revisionHistory);
        }
        if (parsed.pendingRevision && typeof parsed.pendingRevision === 'object') {
          setPendingRevision(parsed.pendingRevision);
        }
        if (parsed.activeTranslatedDocumentId && typeof parsed.activeTranslatedDocumentId === 'string') {
          setActiveTranslatedDocumentId(parsed.activeTranslatedDocumentId);
        }
        if (typeof parsed.includeOriginalContext === 'boolean') {
          setIncludeOriginalContext(parsed.includeOriginalContext);
        }
      }
    } catch (error) {
      console.warn('Failed to restore retranslation session:', error.message);
    }

    if (!hasSessionState && normalizedPersistedRetranslationComments.length > 0) {
      setRetranslationMessages(
        normalizedPersistedRetranslationComments.map((comment, index) => ({
          id: `persisted_user_${index}_${comment.createdAt}`,
          role: 'user',
          content: comment.content,
          timestamp: comment.createdAt
        }))
      );
    }
  }, [translationId, retranslationSessionKey, normalizedPersistedRetranslationComments]);

  // Persist retranslation session per translation in browser session storage
  useEffect(() => {
    if (!translationId) return;
    const payload = {
      messages: retranslationMessages,
      pendingRevision,
      revisionHistory,
      activeTranslatedDocumentId: effectiveTranslatedDocumentId || null,
      includeOriginalContext
    };

    try {
      sessionStorage.setItem(retranslationSessionKey, JSON.stringify(payload));
    } catch (error) {
      console.warn('Failed to persist retranslation session:', error.message);
    }
  }, [
    translationId,
    retranslationMessages,
    pendingRevision,
    revisionHistory,
    effectiveTranslatedDocumentId,
    includeOriginalContext,
    retranslationSessionKey
  ]);

  const clearRetranslationSessionLocal = () => {
    setRetranslationMessages([]);
    setPendingRevision(null);
    setRevisionHistory([]);
    setIncludeOriginalContext(true);
    try {
      sessionStorage.removeItem(retranslationSessionKey);
    } catch (error) {
      console.warn('Failed to clear retranslation session:', error.message);
    }
  };

  const clearRetranslationSession = async () => {
    if (!translationId) {
      clearRetranslationSessionLocal();
      return;
    }

    try {
      await translationApi.clearRetranslationComments(apiClient, translationId);
      clearRetranslationSessionLocal();
    } catch (error) {
      console.error('Failed to clear retranslation session:', error);
      toast.error(error?.response?.data?.error || 'Failed to clear retranslation session.');
    }
  };

  const blobToBase64 = (blob) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const dataUrl = reader.result;
        if (typeof dataUrl !== 'string') {
          reject(new Error('Unable to read exported DOCX'));
          return;
        }
        const base64 = dataUrl.split(',')[1] || '';
        resolve(base64);
      };
      reader.onerror = () => reject(new Error('Failed converting blob to base64'));
      reader.readAsDataURL(blob);
    });
  };

  const base64ToDocxFile = (base64Content, fileName) => {
    const binaryString = atob(base64Content);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i += 1) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return new File(
      [bytes],
      fileName,
      { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' }
    );
  };

  const appendRetranslationMessage = (message) => {
    const nextMessage = {
      id: `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      timestamp: new Date().toISOString(),
      ...message
    };
    setRetranslationMessages((prev) => [...prev, nextMessage]);
    return nextMessage;
  };

  const sanitizeAssistantSummary = (content) => {
    if (!content || typeof content !== 'string') {
      return t('editor.messages.retranslationPreviewFallbackSummary');
    }

    const lines = content
      .replace(/```[\s\S]*?```/g, ' ')
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean)
      .filter((line) => !/^(i('|’)ll|let me|now let me|first[, ]|next[, ]|then[, ]|i will|perfect\b|great\b|starting\b|examining\b|analyzing\b|running\b)/i.test(line));

    if (lines.length === 0) {
      return t('editor.messages.retranslationPreviewFallbackSummary');
    }

    const candidate = lines[0].replace(/\s+/g, ' ').trim();
    return candidate.length > 280 ? `${candidate.slice(0, 277)}...` : candidate;
  };

  const resolveRetranslationErrorMessage = (error) => {
    const apiError = error?.response?.data?.error;

    if (apiError === 'Original document context is unavailable for this translation') {
      return t('editor.messages.retranslationOriginalContextUnavailable');
    }
    if (apiError === 'Invalid DOCX base64 payload' || apiError === 'currentDocxBase64 does not look like a valid DOCX file') {
      return t('editor.messages.retranslationInvalidDocxPayload');
    }
    if (apiError === 'Retranslation request failed because an invalid content format was sent to AI') {
      return t('editor.messages.retranslationInvalidAiPayloadFormat');
    }

    return apiError || error?.message || t('editor.messages.retranslationFailed');
  };

  // Mobile detection effect
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);

      // Force editor-full view on mobile
      if (mobile && viewMode === 'split') {
        setViewMode('editor-full');
      }
    };

    // Check on mount
    checkMobile();

    // Add resize listener
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, [viewMode]);

  // Handle full-screen toggle
  const toggleFullScreen = () => {
    if (!isFullScreen) {
      if (containerRef.current.requestFullscreen) {
        containerRef.current.requestFullscreen();
      } else if (containerRef.current.webkitRequestFullscreen) {
        containerRef.current.webkitRequestFullscreen();
      } else if (containerRef.current.msRequestFullscreen) {
        containerRef.current.msRequestFullscreen();
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen();
      }
    }
  };

  // Listen for fullscreen changes
  useEffect(() => {
    const handleFullScreenChange = () => {
      setIsFullScreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullScreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullScreenChange);
    document.addEventListener('msfullscreenchange', handleFullScreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullScreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullScreenChange);
      document.removeEventListener('msfullscreenchange', handleFullScreenChange);
    };
  }, []);

  // Handle split panel resizing
  const handleMouseDown = (e) => {
    if (viewMode === 'split') {
      setIsDragging(true);
      e.preventDefault();
      e.stopPropagation();
    }
  };

  useEffect(() => {
    if (!isDragging) return;

    let rafId = null;
    let lastX = null;

    const handleMouseMove = (e) => {
      e.preventDefault();
      e.stopPropagation();

      // Store the mouse position
      lastX = e.clientX;

      // Cancel any pending animation frame
      if (rafId) {
        cancelAnimationFrame(rafId);
      }

      // Use requestAnimationFrame for smooth updates
      rafId = requestAnimationFrame(() => {
        if (viewMode === 'split' && containerRef.current && lastX !== null) {
          const containerRect = containerRef.current.getBoundingClientRect();
          const newRatio = ((lastX - containerRect.left) / containerRect.width) * 100;
          const clampedRatio = Math.max(20, Math.min(80, newRatio));

          setSplitRatio(clampedRatio);
        }
      });
    };

    const handleMouseUp = (e) => {
      e.preventDefault();
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
      setIsDragging(false);
    };

    // Add listeners to window instead of document for better event capture
    window.addEventListener('mousemove', handleMouseMove, { capture: true, passive: false });
    window.addEventListener('mouseup', handleMouseUp, { capture: true, passive: false });

    // Prevent text selection and iframe interaction during drag
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'col-resize';
    document.body.style.pointerEvents = 'none'; // Prevent iframes from capturing events

    // Keep the resizer interactive
    if (resizerRef.current) {
      resizerRef.current.style.pointerEvents = 'auto';
    }

    return () => {
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
      window.removeEventListener('mousemove', handleMouseMove, { capture: true });
      window.removeEventListener('mouseup', handleMouseUp, { capture: true });
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
      document.body.style.pointerEvents = '';
      if (resizerRef.current) {
        resizerRef.current.style.pointerEvents = '';
      }
    };
  }, [isDragging, viewMode]);

  // Handle view mode changes
  const handleViewModeChange = (newMode) => {
    // Prevent split view on mobile
    if (isMobile && newMode === 'split') {
      toast.info('Split view is not available on mobile devices');
      return;
    }
    viewModeRef.current = newMode;
    setViewMode(newMode);
  };

  // Update view mode ref when state changes
  useEffect(() => {
    viewModeRef.current = viewMode;
  }, [viewMode]);

  // Open Google Docs in new tab
  const openInGoogleDocs = () => {
    if (!effectiveTranslatedDocumentId) {
      toast.error(t('editor.messages.noDocumentToOpen'));
      return;
    }
    window.open(`https://docs.google.com/document/d/${effectiveTranslatedDocumentId}/edit`, '_blank');
  };

  const sanitizeBaseName = (rawName, fallback = 'translation') => {
    const baseName = (rawName || fallback).replace(/\.[^/.]+$/, '').trim();
    const safe = baseName.replace(/[^a-z0-9_-]/gi, '_').replace(/_+/g, '_');
    return safe || fallback;
  };

  // Export Google Doc to PDF and download
  const handleExportToPdf = async () => {
    if (!effectiveTranslatedDocumentId) {
      toast.error(t('editor.messages.noDocumentToExport'));
      return;
    }

    if (!googleAccessToken) {
      toast.error(t('editor.messages.signInGoogleFirst'));
      return;
    }

    setIsExporting(true);

    try {
      const googleDocs = new GoogleDocsService(googleAccessToken);
      const pdfBlob = await googleDocs.exportToPdf(effectiveTranslatedDocumentId);
      let liveTitle = null;
      try {
        const metadata = await googleDocs.getFileMetadata(effectiveTranslatedDocumentId);
        liveTitle = metadata?.name || null;
      } catch (metadataError) {
        console.warn('Could not fetch live Google Doc title for PDF export:', metadataError.message);
      }

      // Generate filename based on document title
      const safeBaseName = sanitizeBaseName(liveTitle || documentTitle, 'translation');

      // Create download link
      const url = window.URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${safeBaseName}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success(t('editor.messages.pdfExportedSuccess'));
    } catch (error) {
      console.error('Error exporting to PDF:', error);
      toast.error(`Failed to export PDF: ${error.message}`);
    } finally {
      setIsExporting(false);
    }
  };

  // Mark translation as ready for approval
  const handleMarkReadyForApproval = async () => {
    if (!translationId) {
      toast.error(t('editor.messages.noTranslationId'));
      return;
    }

    setIsMarkingReady(true);

    try {
      await translationApi.markReadyForApproval(apiClient, translationId);
      setIsReadyForApproval(true); // Update local state to hide button
      toast.success(t('editor.messages.markedReadySuccess'));
    } catch (error) {
      console.error('Error marking translation as ready for approval:', error);
      toast.error(t('editor.messages.markReadyFailed'));
    } finally {
      setIsMarkingReady(false);
    }
  };

  // Open approval modal
  const openApprovalModal = () => {
    if (!effectiveTranslatedDocumentId) {
      toast.error(t('editor.messages.noDocumentToExport'));
      return;
    }

    if (!googleAccessToken) {
      toast.error(t('editor.messages.signInGoogleFirst'));
      return;
    }

    if (!translationId) {
      toast.error(t('editor.messages.noTranslationId'));
      return;
    }

    setShowApprovalModal(true);
  };

  // Export Google Doc to PDF and upload to Dropbox for approval
  const exportPdfForApproval = async () => {
    try {
      if (!effectiveTranslatedDocumentId || !googleAccessToken) {
        throw new Error(t('editor.messages.missingGoogleDoc'));
      }

      // Step 1: Export Google Doc to PDF (Blob)
      const googleDocs = new GoogleDocsService(googleAccessToken);
      let googleDocTitle = null;
      try {
        const metadata = await googleDocs.getFileMetadata(effectiveTranslatedDocumentId);
        googleDocTitle = metadata?.name || null;
      } catch (metadataError) {
        console.warn(`Could not fetch live Google title for ${effectiveTranslatedDocumentId}:`, metadataError.message);
      }
      const pdfBlob = await googleDocs.exportToPdf(effectiveTranslatedDocumentId);

      // Step 2: Generate filename based on document title
      const baseName = (googleDocTitle || documentTitle)?.replace(/\.[^/.]+$/, '') || `translation_${translationId}`;
      const safeBaseName = baseName.replace(/[^a-z0-9_-]/gi, '_');

      // Step 3: Upload PDF to Dropbox via backend
      const formData = new FormData();
      formData.append('documentId', translationId);  // translationId prop is actually a documentId
      if (googleDocTitle) {
        formData.append('googleDocTitle', googleDocTitle);
      }
      formData.append('file', pdfBlob, `${safeBaseName}_approval.pdf`);

      const uploadResponse = await apiClient.post('/approvals/upload-pdf', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (!uploadResponse.data?.success) {
        throw new Error(uploadResponse.data?.error || t('editor.messages.uploadApprovalFailed'));
      }

      return {
        approvalPdfPath: uploadResponse.data.dropboxPath,
        googleDocTitle,
        success: true
      };
    } catch (error) {
      console.error('Error exporting PDF for approval:', error);
      throw error;
    }
  };

  // Handle request approval submission
  const handleRequestApproval = async () => {
    // Validate inputs
    if (!approverName.trim()) {
      toast.error(t('editor.messages.enterApproverName'));
      return;
    }

    if (!approverEmail.trim()) {
      toast.error(t('editor.messages.enterApproverEmail'));
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(approverEmail)) {
      toast.error(t('editor.messages.enterValidEmail'));
      return;
    }

    const approverEmailTrimmed = approverEmail.trim();
    setIsSubmitting(true);

    try {
      const googleDocs = new GoogleDocsService(googleAccessToken);
      let googleSentEmail = false;

      // Step 1: Share document without notification; retry with notification if required
      try {
        console.log('Attempting to share without Google notification...');
        await googleDocs.shareDocument(effectiveTranslatedDocumentId, approverEmailTrimmed, 'commenter', false);
        console.log('✓ Shared without Google notification');
      } catch (shareError) {
        if (shareError.message?.includes('Notify people') ||
            shareError.response?.data?.error?.message?.includes('Notify people') ||
            shareError.response?.status === 400) {
          console.log('Email requires Google notification, retrying...');
          await googleDocs.shareDocument(effectiveTranslatedDocumentId, approverEmailTrimmed, 'commenter', true);
          googleSentEmail = true;
          console.log('✓ Shared with Google notification (user will receive Google sharing email)');
        } else {
          throw shareError;
        }
      }

      // Step 2: Export PDF and upload to Dropbox
      toast.info('Preparing document for approval...');
      let approvalPdfPath = null;
      let googleDocTitle = null;
      let webViewLink = null;

      try {
        const exportResult = await exportPdfForApproval();
        approvalPdfPath = exportResult.approvalPdfPath;
        googleDocTitle = exportResult.googleDocTitle || null;

        // Get Google Docs sharing link
        webViewLink = `https://docs.google.com/document/d/${effectiveTranslatedDocumentId}/edit`;

        console.log('✅ PDF uploaded to Dropbox:', approvalPdfPath);
      } catch (exportError) {
        console.error('Failed to export PDF:', exportError);
        toast.error('Failed to prepare PDF for approval. Please try again.');
        setIsSubmitting(false);
        return;
      }

      // Step 3: Create approval request via backend
      // Send documentId instead of translationId (translationId prop is actually documentId)
      // Backend now supports both documentId (new) and translationId (legacy for backwards compatibility)
      const response = await apiClient.post('/approvals', {
        translations: [{
          documentId: translationId,  // translationId prop is actually a documentId
          googleDocId: effectiveTranslatedDocumentId,
          webViewLink: webViewLink,              // Google Docs sharing link for commenting
          approvalPdfPath: approvalPdfPath,      // Dropbox path for PDF viewing
          googleDocTitle: googleDocTitle || null
        }],
        approverName: approverName.trim(),
        approverEmail: approverEmail.trim(),
        language: approverLanguage,
        translatorName: userProfile?.name || user?.name || ''
      });

      if (response.data.success) {
        // Backend returns results array with approval info
        const firstResult = response.data.results?.[0];

        if (firstResult && firstResult.success) {
          const approvalLink = firstResult.approvalLink;

          console.log('📋 Client Approval Link:', approvalLink);
          console.log('👉 Copy this link and send to client:', approverEmail);
          console.log('📄 Document ID:', effectiveTranslatedDocumentId);
          console.log('✅ Approval created:', firstResult.approval);

          // Show appropriate success message based on whether Google sent an email
          if (googleSentEmail) {
            toast.success(`Approval request sent! Note: Client will receive 2 emails (one from Google for document access, one from us with the approval link).`);
          } else {
            toast.success(`Approval request sent! Client will receive our custom approval email.`);
          }

          // Close modal and reset form
          setShowApprovalModal(false);
          setApproverName('');
          setApproverEmail('');
        } else {
          throw new Error(firstResult?.error || t('editor.messages.approvalRequestFailed'));
        }
      } else {
        throw new Error(response.data.error || response.data.message || t('editor.messages.approvalRequestFailed'));
      }
    } catch (error) {
      console.error('Error creating approval request:', error);
      toast.error(`Failed to create approval request: ${error.response?.data?.error || error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSendRetranslationPrompt = async () => {
    const trimmedPrompt = retranslationInput.trim();
    if (!trimmedPrompt) {
      toast.error(t('editor.messages.retranslationPromptRequired'));
      return;
    }

    if (!translationId) {
      toast.error(t('editor.messages.noTranslationId'));
      return;
    }

    if (!effectiveTranslatedDocumentId) {
      toast.error(t('editor.messages.noDocumentToOpen'));
      return;
    }

    if (!googleAccessToken) {
      toast.error(t('editor.messages.signInGoogleFirst'));
      return;
    }

    if (includeOriginalContext && !hasOriginalDocumentContext) {
      toast.error(t('editor.messages.retranslationOriginalContextUnavailable'));
      return;
    }

    appendRetranslationMessage({
      role: 'user',
      content: trimmedPrompt
    });

    setIsRetranslating(true);
    setRetranslationInput('');

    try {
      const googleDocs = new GoogleDocsService(googleAccessToken);
      const currentDocxBlob = await googleDocs.exportToDocx(effectiveTranslatedDocumentId);
      const currentDocxBase64 = await blobToBase64(currentDocxBlob);

      const conversationPayload = retranslationMessages
        .filter((message) => message.role === 'user')
        .map((message) => ({
          role: 'user',
          content: message.content,
          createdAt: message.timestamp || message.createdAt
        }));

      const response = await apiClient.post(
        `/translations/${translationId}/retranslation/chat`,
        {
          documentId: translationId,
          currentDocxBase64,
          userPrompt: trimmedPrompt,
          conversationHistory: conversationPayload,
          sourceLanguage: sourceLanguage || undefined,
          targetLanguage: targetLanguage || undefined,
          additionalContext: additionalContext || undefined,
          includeOriginalContext
        }
      );

      const result = response?.data;
      if (!result?.success || !result?.revisedDocxBase64) {
        throw new Error(result?.error || t('editor.messages.retranslationFailed'));
      }

      setPendingRevision({
        revisedDocxBase64: result.revisedDocxBase64,
        explanation: sanitizeAssistantSummary(result.explanation || ''),
        changes: Array.isArray(result.changes) ? result.changes : [],
        requestedPrompt: trimmedPrompt,
        generatedAt: new Date().toISOString()
      });

      toast.success(t('editor.messages.retranslationPreviewReady'));
    } catch (error) {
      console.error('Error generating retranslation preview:', error);
      appendRetranslationMessage({
        role: 'assistant',
        content: t('editor.messages.retranslationFailed')
      });
      toast.error(resolveRetranslationErrorMessage(error));
    } finally {
      setIsRetranslating(false);
    }
  };

  const handleApplyRetranslationRevision = async () => {
    if (!pendingRevision?.revisedDocxBase64) {
      toast.error(t('editor.messages.noRetranslationToApply'));
      return;
    }

    if (!translationId || !googleAccessToken) {
      toast.error(t('editor.messages.signInGoogleFirst'));
      return;
    }

    setIsApplyingRevision(true);
    try {
      const revisionIndex = revisionHistory.length + 1;
      const revisionTitle = `${sanitizeBaseName(documentTitle, 'translation')}_revision_${revisionIndex}`;
      const revisionFile = base64ToDocxFile(
        pendingRevision.revisedDocxBase64,
        `${revisionTitle}.docx`
      );

      const googleDocs = new GoogleDocsService(googleAccessToken);
      const imported = await googleDocs.importDocx(revisionFile, revisionTitle);
      const nextGoogleDocId = imported.documentId;

      if (!nextGoogleDocId) {
        throw new Error(t('editor.messages.retranslationApplyFailed'));
      }

      const previousDocId = effectiveTranslatedDocumentId;
      setActiveTranslatedDocumentId(nextGoogleDocId);

      await apiClient.post(`/translations/${translationId}/google-doc-id`, {
        googleDocId: nextGoogleDocId
      });

      setRevisionHistory((prev) => [
        {
          id: `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
          previousDocId,
          newDocId: nextGoogleDocId,
          requestedPrompt: pendingRevision.requestedPrompt,
          createdAt: new Date().toISOString()
        },
        ...prev
      ]);

      appendRetranslationMessage({
        role: 'assistant',
        content: t('editor.messages.retranslationApplied')
      });

      setPendingRevision(null);
      toast.success(t('editor.messages.retranslationApplied'));
    } catch (error) {
      console.error('Error applying retranslation revision:', error);
      toast.error(error?.response?.data?.error || error?.message || t('editor.messages.retranslationApplyFailed'));
    } finally {
      setIsApplyingRevision(false);
    }
  };

  const handleUndoLastRetranslationApply = async () => {
    if (!translationId || !googleAccessToken) {
      toast.error(t('editor.messages.signInGoogleFirst'));
      return;
    }

    if (revisionHistory.length === 0) {
      toast.error(t('editor.messages.retranslationUndoUnavailable'));
      return;
    }

    const [latestRevision, ...remainingRevisions] = revisionHistory;
    if (!latestRevision?.previousDocId) {
      toast.error(t('editor.messages.retranslationUndoFailed'));
      return;
    }

    setIsUndoingRevision(true);
    try {
      await apiClient.post(`/translations/${translationId}/google-doc-id`, {
        googleDocId: latestRevision.previousDocId
      });

      setActiveTranslatedDocumentId(latestRevision.previousDocId);
      setRevisionHistory(remainingRevisions);

      appendRetranslationMessage({
        role: 'assistant',
        content: t('editor.messages.retranslationUndoApplied')
      });

      toast.success(t('editor.messages.retranslationUndoApplied'));
    } catch (error) {
      console.error('Error undoing last retranslation apply:', error);
      toast.error(error?.response?.data?.error || error?.message || t('editor.messages.retranslationUndoFailed'));
    } finally {
      setIsUndoingRevision(false);
    }
  };

  const renderRetranslationDrawer = () => (
    <div className={`absolute top-0 right-0 h-full z-[110] bg-white border-l border-gray-200 shadow-2xl transition-transform duration-200 ${showRetranslationDrawer ? 'translate-x-0' : 'translate-x-full'} w-full sm:w-[420px]`}>
      <div className="h-full flex flex-col">
        <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">{t('editor.actions.aiRetranslate')}</h3>
            <p className="text-xs text-gray-500">{sourceLanguage || '—'} → {targetLanguage || '—'}</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="text-xs text-gray-500 hover:text-gray-700"
              onClick={clearRetranslationSession}
            >
              {t('editor.actions.clearSession')}
            </button>
            <button
              type="button"
              className="text-gray-500 hover:text-gray-700 text-lg leading-none"
              onClick={() => setShowRetranslationDrawer(false)}
              aria-label={t('common.close')}
            >
              ×
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3 bg-gray-50">
          {retranslationMessages.length === 0 && (
            <p className="text-sm text-gray-500">{t('editor.messages.retranslationHint')}</p>
          )}

          {retranslationMessages.map((message) => (
            <div
              key={message.id}
              className={`rounded-lg px-3 py-2 text-sm ${message.role === 'user' ? 'bg-blue-600 text-white ml-6' : 'bg-white border border-gray-200 text-gray-800 mr-6'}`}
            >
              {message.role === 'assistant' ? (
                <>
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">{t('editor.messages.retranslationSummaryLabel')}</p>
                  <p className="whitespace-pre-wrap mt-1">{sanitizeAssistantSummary(message.content)}</p>
                </>
              ) : (
                <p className="whitespace-pre-wrap">{message.content}</p>
              )}
              {Array.isArray(message.changes) && message.changes.length > 0 && (
                <div className="mt-2">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">{t('editor.messages.retranslationChangesLabel')}</p>
                  <ul className="mt-1 text-xs list-disc pl-4 space-y-1">
                  {message.changes.slice(0, 5).map((change, index) => (
                    <li key={`${message.id}_change_${index}`}>{change}</li>
                  ))}
                  </ul>
                </div>
              )}
            </div>
          ))}

          {pendingRevision && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
              <p className="text-sm font-semibold text-emerald-900">{t('editor.messages.retranslationPreviewReady')}</p>
              <p className="text-[11px] font-semibold uppercase tracking-wide text-emerald-700 mt-2">{t('editor.messages.retranslationSummaryLabel')}</p>
              <p className="text-xs text-emerald-800 mt-1">{sanitizeAssistantSummary(pendingRevision.explanation)}</p>
              {pendingRevision.changes?.length > 0 && (
                <div className="mt-2">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-emerald-700">{t('editor.messages.retranslationChangesLabel')}</p>
                  <ul className="mt-1 text-xs text-emerald-900 list-disc pl-4 space-y-1">
                  {pendingRevision.changes.slice(0, 5).map((change, index) => (
                    <li key={`pending_change_${index}`}>{change}</li>
                  ))}
                  </ul>
                </div>
              )}
              <div className="mt-3 flex items-center gap-2">
                <button
                  type="button"
                  onClick={handleApplyRetranslationRevision}
                  disabled={isApplyingRevision || isUndoingRevision}
                  className="px-3 py-1.5 rounded bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isApplyingRevision ? t('editor.actions.applying') : t('editor.actions.applyRevision')}
                </button>
                <button
                  type="button"
                  onClick={() => setPendingRevision(null)}
                  disabled={isApplyingRevision || isUndoingRevision}
                  className="px-3 py-1.5 rounded border border-gray-300 text-gray-700 text-xs font-semibold hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {t('editor.actions.discardPreview')}
                </button>
              </div>
            </div>
          )}

          {revisionHistory.length > 0 && (
            <div className="bg-white border border-gray-200 rounded-lg p-3">
              <div className="mb-2 flex items-center justify-between gap-2">
                <p className="text-xs font-semibold text-gray-700">{t('editor.messages.revisionHistory')}</p>
                <button
                  type="button"
                  onClick={handleUndoLastRetranslationApply}
                  disabled={isUndoingRevision || isApplyingRevision || revisionHistory.length === 0}
                  className="px-2 py-1 text-[11px] font-semibold rounded border border-gray-300 text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isUndoingRevision ? t('editor.actions.undoingLastApply') : t('editor.actions.undoLastApply')}
                </button>
              </div>
              <div className="space-y-1">
                {revisionHistory.slice(0, 6).map((revision) => (
                  <button
                    key={revision.id}
                    type="button"
                    onClick={() => window.open(`https://docs.google.com/document/d/${revision.newDocId}/edit`, '_blank')}
                    className="w-full text-left text-xs text-blue-700 hover:underline"
                  >
                    {new Date(revision.createdAt).toLocaleString()} - {revision.newDocId}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="border-t border-gray-200 p-3 bg-white">
          <label className="mb-2 flex items-start gap-2 text-xs text-gray-700">
            <input
              type="checkbox"
              checked={includeOriginalContext}
              onChange={(event) => setIncludeOriginalContext(event.target.checked)}
              disabled={isRetranslating}
              className="mt-0.5 h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500 disabled:opacity-60"
            />
            <span>
              <span className="font-medium">{t('editor.messages.retranslationIncludeOriginalContext')}</span>
              <span className={`block mt-0.5 ${includeOriginalContext && !hasOriginalDocumentContext ? 'text-red-600' : 'text-gray-500'}`}>
                {includeOriginalContext && !hasOriginalDocumentContext
                  ? t('editor.messages.retranslationOriginalContextUnavailable')
                  : t('editor.messages.retranslationIncludeOriginalContextHelp')}
              </span>
            </span>
          </label>
          <textarea
            rows={3}
            value={retranslationInput}
            onChange={(event) => setRetranslationInput(event.target.value)}
            placeholder={t('editor.messages.retranslationPlaceholder')}
            className="w-full border border-gray-300 rounded-md p-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
            disabled={isRetranslating}
          />
          <div className="mt-2 flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={() => setShowRetranslationDrawer(false)}
              className="px-3 py-1.5 border border-gray-300 rounded text-xs text-gray-700 hover:bg-gray-100"
            >
              {t('common.close')}
            </button>
            <button
              type="button"
              onClick={handleSendRetranslationPrompt}
              disabled={isRetranslating || isUndoingRevision || !retranslationInput.trim()}
              className="px-3 py-1.5 rounded bg-blue-600 text-white text-xs font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isRetranslating ? t('editor.actions.generating') : t('editor.actions.generateRevision')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  const renderToolbar = () => (
    <div className="sticky top-0 z-[100] w-full flex items-center justify-between bg-gray-800 text-white px-2 sm:px-3 py-1 sm:py-1.5 border-b border-gray-700 shadow-md">
      {/* Mobile View Toggle - Only on mobile */}
      {isMobile && (
        <div className="flex items-center bg-gray-900 rounded-md p-0.5">
          <button
            className={`px-2 py-1 rounded text-xs font-medium transition-all ${
              viewMode === 'original-full'
                ? 'bg-blue-500 text-white shadow-sm'
                : 'text-gray-400 hover:text-gray-200 hover:bg-gray-800'
            }`}
            onClick={() => handleViewModeChange('original-full')}
            title="Original"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </button>
          <button
            className={`px-2 py-1 rounded text-xs font-medium transition-all ${
              viewMode === 'editor-full'
                ? 'bg-blue-500 text-white shadow-sm'
                : 'text-gray-400 hover:text-gray-200 hover:bg-gray-800'
            }`}
            onClick={() => handleViewModeChange('editor-full')}
            title="Editor"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
          </button>
        </div>
      )}

      {/* View Mode Toggle - Hidden on mobile */}
      {!isMobile && (
        <div className="flex items-center gap-2">
          <div className="flex items-center bg-gray-900 rounded-md p-0.5">
            <button
              className={`px-2 py-1 rounded text-xs font-medium transition-all ${
                viewMode === 'split'
                  ? 'bg-blue-500 text-white shadow-sm'
                  : 'text-gray-400 hover:text-gray-200 hover:bg-gray-800'
              }`}
              onClick={() => handleViewModeChange('split')}
              title={t('editor.viewModes.split')}
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 4H5a1 1 0 00-1 1v14a1 1 0 001 1h4m0-16v16m0-16h6m-6 16h6m0-16v16m0-16h4a1 1 0 011 1v14a1 1 0 01-1 1h-4" />
              </svg>
            </button>
            <button
              className={`px-2 py-1 rounded text-xs font-medium transition-all ${
                viewMode === 'original-full'
                  ? 'bg-blue-500 text-white shadow-sm'
                  : 'text-gray-400 hover:text-gray-200 hover:bg-gray-800'
              }`}
              onClick={() => handleViewModeChange('original-full')}
              title={t('editor.viewModes.originalFull')}
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </button>
            <button
              className={`px-2 py-1 rounded text-xs font-medium transition-all ${
                viewMode === 'editor-full'
                  ? 'bg-blue-500 text-white shadow-sm'
                  : 'text-gray-400 hover:text-gray-200 hover:bg-gray-800'
              }`}
              onClick={() => handleViewModeChange('editor-full')}
              title={t('editor.viewModes.editorFull')}
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Document Title */}
      <div className={`flex-1 ${isMobile ? 'text-left' : 'text-center'} px-2 sm:px-4`}>
        <h3 className="text-xs sm:text-sm font-semibold text-white truncate">
          {documentTitle}
        </h3>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-1 sm:gap-1.5">
        {saving && (
          <div className="flex items-center gap-1 text-gray-300 text-[10px] bg-gray-700 bg-opacity-50 px-1.5 py-0.5 rounded">
            <div className="w-2.5 h-2.5 border-2 border-gray-300 border-t-transparent rounded-full animate-spin"></div>
            <span className="hidden sm:inline">{t('editor.actions.saving')}</span>
          </div>
        )}
        {!isReadyForApproval && (
          <button
            className="px-1.5 sm:px-2.5 py-1 bg-orange-600 text-white rounded text-[10px] sm:text-xs font-medium hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-0.5 sm:gap-1"
            onClick={handleMarkReadyForApproval}
            disabled={saving || isMarkingReady}
            title={t('editor.actions.readyForApprovalTitle')}
          >
            {isMarkingReady ? (
              <>
                <div className="w-3 sm:w-3.5 h-3 sm:h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                <span className="hidden sm:inline">{t('editor.actions.saving')}</span>
              </>
            ) : (
              <>
                <svg className="w-3 sm:w-3.5 h-3 sm:h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="hidden md:inline">{t('editor.actions.readyForApproval')}</span>
                <span className="md:hidden">Ready</span>
              </>
            )}
          </button>
        )}
        <button
          className="px-1.5 sm:px-2.5 py-1 bg-purple-300 text-blue-800 hover:bg-purple-400 rounded text-[10px] sm:text-xs font-medium hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-0.5 sm:gap-1"
          onClick={openApprovalModal}
          disabled={saving}
          title={t('editor.actions.requestApprovalTitle')}
        >
          <svg className="w-3 sm:w-3.5 h-3 sm:h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
          </svg>
          <span className="hidden lg:inline">{t('editor.actions.requestApproval')}</span>
          <span className="lg:hidden">Share</span>
        </button>
        <button
          className="px-1.5 sm:px-2.5 py-1 bg-emerald-600 text-white rounded text-[10px] sm:text-xs font-medium hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-0.5 sm:gap-1"
          onClick={() => setShowRetranslationDrawer((prev) => !prev)}
          disabled={!effectiveTranslatedDocumentId || saving || isRetranslating}
          title={t('editor.actions.aiRetranslate')}
        >
          <svg className="w-3 sm:w-3.5 h-3 sm:h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v12m6-6H6m11-7l1.5 1.5M5 19l1.5-1.5M19 19l-1.5-1.5M5 5l1.5 1.5" />
          </svg>
          <span className="hidden lg:inline">{t('editor.actions.aiRetranslate')}</span>
          <span className="lg:hidden">AI</span>
        </button>
        <button
          className="px-1.5 sm:px-2.5 py-1 bg-green-300 text-blue-800 rounded text-[10px] sm:text-xs font-medium hover:bg-green-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-0.5 sm:gap-1"
          onClick={openInGoogleDocs}
          disabled={!effectiveTranslatedDocumentId}
          title={t('editor.actions.openInDocsTitle')}
        >
          <svg className="w-3 sm:w-3.5 h-3 sm:h-3.5" viewBox="0 0 24 24" fill="currentColor">
            <path d="M14.727 6.727H14V0H4.91c-.905 0-1.637.732-1.637 1.636v20.728c0 .904.732 1.636 1.636 1.636h14.182c.904 0 1.636-.732 1.636-1.636V6.727h-6zM20 23.273H4V.727h9v6.818c0 .45.368.818.818.818H20v14.91zM14.727 1.454v5.273h5.273l-5.273-5.273z" />
          </svg>
          <span className="hidden lg:inline">{t('editor.actions.openInDocs')}</span>
          <span className="lg:hidden">Open</span>
        </button>
        <button
          className="px-1.5 sm:px-2.5 py-1 bg-amber-500 text-white rounded text-[10px] sm:text-xs font-medium hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-0.5 sm:gap-1"
          onClick={onSign}
          disabled={!onSign || saving || isExporting || !effectiveTranslatedDocumentId}
          title={latestFinalDocument ? `Create a new signed version from v${latestFinalDocument.version}` : 'Sign this document'}
        >
          <svg className="w-3 sm:w-3.5 h-3 sm:h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931z" />
          </svg>
          <span className="hidden lg:inline">
            {latestFinalDocument ? `Re-sign v${latestFinalDocument.version}` : 'Sign'}
          </span>
          <span className="lg:hidden">Sign</span>
        </button>
        <button
          className="px-1.5 sm:px-2.5 py-1 bg-blue-600 text-white rounded text-[10px] sm:text-xs font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-0.5 sm:gap-1"
          onClick={handleExportToPdf}
          disabled={saving || isExporting || !effectiveTranslatedDocumentId}
          title={t('editor.actions.exportPdfTitle')}
        >
          {isExporting ? (
            <>
              <div className="w-3 sm:w-3.5 h-3 sm:h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              <span className="hidden sm:inline">{t('editor.actions.exporting')}</span>
            </>
          ) : (
            <>
              <svg className="w-3 sm:w-3.5 h-3 sm:h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              <span className="hidden lg:inline">{t('editor.actions.exportPdf')}</span>
              <span className="lg:hidden">PDF</span>
            </>
          )}
        </button>
        <button
          className="p-0.5 sm:p-1 bg-gray-700 text-gray-300 rounded hover:bg-gray-600 transition-colors"
          onClick={toggleFullScreen}
          title={isFullScreen ? t('editor.actions.exitFullscreen') : t('editor.actions.enterFullscreen')}
        >
          <svg className="w-3 sm:w-3.5 h-3 sm:h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            {isFullScreen ? (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 9V4.5M9 9H4.5M9 9L3.75 3.75M9 15v4.5M9 15H4.5M9 15l-5.25 5.25M15 9h4.5M15 9V4.5M15 9l5.25-5.25M15 15h4.5M15 15v4.5m0-4.5l5.25 5.25" />
            ) : (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m5.25 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15" />
            )}
          </svg>
        </button>
      </div>
    </div>
  );

  const renderSplitView = () => (
    <div className="flex flex-row h-full min-h-0" style={{ height: '100%' }}>
      {/* Original Document Panel */}
      <div
        className="bg-white border-r border-gray-300 flex flex-col min-h-0"
        style={{ width: `${splitRatio}%` }}
      >
        <div className="flex-1 min-h-0 overflow-auto">
          {originalDocument || originalDocumentId ? (
            <div
              className="h-full w-full"
              ref={(el) => {
                if (el && documentViewerRef.current) {
                  const persistentViewer = documentViewerRef.current;
                  persistentViewer.style.position = 'static';
                  persistentViewer.style.top = 'auto';
                  persistentViewer.style.left = 'auto';
                  persistentViewer.style.visibility = 'visible';
                  if (persistentViewer.parentNode !== el) {
                    el.appendChild(persistentViewer);
                  }
                }
              }}
            />
          ) : (
            <div className="flex items-center justify-center h-full bg-gray-50">
              <div className="text-center">
                <div className="text-4xl mb-2">📄</div>
                <p className="text-gray-500">{t('editor.messages.originalNotAvailable')}</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Resizer */}
      <div
        ref={resizerRef}
        className="relative w-1 bg-gray-300 hover:bg-blue-500 cursor-col-resize flex items-center justify-center transition-colors group"
        onMouseDown={handleMouseDown}
        style={{
          minWidth: '4px',
          paddingLeft: '4px',
          paddingRight: '4px',
          marginLeft: '-4px',
          marginRight: '-4px'
        }}
      >
        <div className="absolute inset-y-0 left-1/2 transform -translate-x-1/2 w-1 bg-gray-300 group-hover:bg-blue-500 transition-colors"></div>
        <div className="text-gray-600 text-xs select-none pointer-events-none relative z-10">⋮⋮</div>
      </div>

      {/* Google Docs Editor Panel */}
      <div
        className="bg-white flex flex-col min-h-0"
        style={{ width: `${100 - splitRatio}%` }}
      >
        <div className="flex-1 min-h-0 overflow-hidden">
          <GoogleDocsEditor
            documentId={effectiveTranslatedDocumentId}
            height="100%"
            showToolbar={true}
          />
        </div>
      </div>
    </div>
  );

  const renderOriginalFullView = () => (
    <div className="bg-white flex flex-col h-full w-full">
      <div className="flex-1 min-h-0 overflow-hidden w-full">
        {originalDocument || originalDocumentId ? (
          <div
            className="h-full w-full"
            ref={(el) => {
              if (el && documentViewerRef.current) {
                const persistentViewer = documentViewerRef.current;
                persistentViewer.style.position = 'static';
                persistentViewer.style.top = 'auto';
                persistentViewer.style.left = 'auto';
                persistentViewer.style.visibility = 'visible';
                if (persistentViewer.parentNode !== el) {
                  el.appendChild(persistentViewer);
                }
              }
            }}
          />
        ) : (
          <div className="flex items-center justify-center h-full bg-gray-50">
            <div className="text-center">
              <div className="text-6xl mb-4">📄</div>
              <h3 className="text-xl font-semibold text-gray-700 mb-2">{t('editor.messages.originalNotAvailable')}</h3>
              <p className="text-gray-500 mb-4">{t('editor.messages.switchToEditor')}</p>
              <button
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                onClick={() => handleViewModeChange('editor-full')}
              >
                Open Editor
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const renderEditorFullView = () => (
    <div className="bg-white flex flex-col h-full w-full">
      <div className="flex-1 min-h-0 overflow-hidden w-full">
        <GoogleDocsEditor
          documentId={effectiveTranslatedDocumentId}
          height="100%"
          showToolbar={true}
        />
      </div>
    </div>
  );

  return (
    <div
      ref={containerRef}
      className={`flex flex-col bg-gray-100 relative w-full ${isFullScreen ? 'fixed inset-0 z-50 h-screen' : 'min-h-screen h-screen z-40'}`}
      style={isFullScreen ? { position: 'relative', zIndex: 2147483647 } : {}}
    >
      {renderToolbar()}

      {/* Persistent DocumentViewer - always rendered but hidden when not needed */}
      <div
        ref={documentViewerRef}
        className="absolute -top-[9999px] -left-[9999px] w-full h-full invisible"
      >
        {originalDocument && !originalDocumentId && (
          <DocumentViewer
            documentUrl={originalDocument}
            title={documentTitle}
            documentType={documentType}
            showPageControls={false}
            flexibleHeight={true}
          />
        )}
        {originalDocumentId && (
          <GoogleDocsEditor
            documentId={originalDocumentId}
            height="100%"
            showToolbar={false}
          />
        )}
      </div>

      <div className="relative flex-1 min-h-0 w-full">
        {viewMode === 'split' && renderSplitView()}
        {viewMode === 'original-full' && renderOriginalFullView()}
        {viewMode === 'editor-full' && renderEditorFullView()}
        {renderRetranslationDrawer()}
      </div>

      <ApprovalRequestModal
        isOpen={showApprovalModal}
        title="Request Client Approval"
        description="Enter the client's details to send an approval request"
        noteText="📋 The client will receive a link to review and comment on the translated document in Google Docs."
        approverName={approverName}
        approverEmail={approverEmail}
        approverLanguage={approverLanguage}
        onFieldChange={(e) => {
          if (e.target.name === 'approverName') {
            setApproverName(e.target.value);
          } else if (e.target.name === 'approverEmail') {
            setApproverEmail(e.target.value);
          } else if (e.target.name === 'approverLanguage') {
            setApproverLanguage(e.target.value);
          }
        }}
        onCancel={() => setShowApprovalModal(false)}
        onSubmit={handleRequestApproval}
        isSubmitting={isSubmitting}
        submitLabel="Send Request"
        accent="purple"
      />
    </div>
  );
};

export default FullScreenGoogleDocsViewer;
